import React from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import AirIcon from "@mui/icons-material/Air";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";

interface CardAction {
  id: string;
  label: string;
  params?: any;
  followup_message?: string;
}

interface WellnessCardProps {
  card: any;
  onAction?: (action: CardAction) => void;
}

const iconByLabel = (label: string) => {
  const key = (label || "").toLowerCase();
  if (key.includes("breathing")) return AirIcon;
  if (key.includes("guided") || key.includes("relax")) return SpaOutlinedIcon;
  if (key.includes("instant") || key.includes("video")) return VideocamOutlinedIcon;
  if (key.includes("schedule") || key.includes("calendar") || key.includes("booking")) return CalendarMonthOutlinedIcon;
  if (
    key.includes("policy document") ||
    key.includes("tax certificate") ||
    key.includes("kotak e-term plan") ||
    key.includes("kotak health shield") ||
    key.includes("document")
  ) {
    return DescriptionOutlinedIcon;
  }
  if (key.includes("call now") || key.includes("call")) return CallOutlinedIcon;
  if (key.includes("full body") || key.includes("book_full_body_test")) return AccessibilityNewOutlinedIcon;
  if (key.includes("blood test") || key.includes("blood")) return ScienceOutlinedIcon;
  if (key.includes("sleep")) return BedtimeOutlinedIcon;
  if (key.includes("activity") || key.includes("walk") || key.includes("exercise")) {
    return DirectionsRunOutlinedIcon;
  }
  if (key.includes("affirmation")) return FavoriteBorderIcon;
  if (key.includes("doctor") || key.includes("consultation")) return LocalHospitalOutlinedIcon;
  return FavoriteBorderIcon;
};

const WellnessCard: React.FC<WellnessCardProps> = ({ card, onAction }) => {
  const handleItemAction = (action?: CardAction) => {
    if (!action) return;
    onAction?.(action);
  };

  const sections = card?.sections || [];

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid #E5E7EB",
        borderRadius: { xs: "12px", lg: "14px" },
        p: { xs: 1.5, md: 2, lg: 2.5 },
        bgcolor: "#ffffff",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 20, md: 24 }, color: "#111827", lineHeight: 1.2 }}>
            {card?.title || "Wellness"}
          </Typography>
          {card?.subtitle && (
            <Typography sx={{ fontSize: { xs: 12, md: 14 }, color: "#B4536A", mt: 0.25 }}>
              {card.subtitle}
            </Typography>
          )}
        </Box>

        {sections.map((section: any, sIdx: number) => (
          <Box key={`wellness_section_${sIdx}`} sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {section?.heading && (
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
                {section.heading}
              </Typography>
            )}

            {(section?.items || []).map((item: any, iIdx: number) => {
              const Icon = iconByLabel(item?.label || "");
              return (
                <Paper
                  key={`wellness_item_${sIdx}_${iIdx}`}
                  elevation={0}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    p: { xs: 1.25, md: 1.5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: { xs: 32, md: 36 },
                        height: { xs: 32, md: 36 },
                        borderRadius: "50%",
                        border: "1px solid #F1D5DB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#C94F68",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: { xs: 17, md: 19 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, md: 14 }, color: "#1F2937", textTransform: "capitalize" }}>
                        {item?.label}
                      </Typography>
                      {item?.value && (
                        <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#6B7280" }}>
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {item?.action && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleItemAction(item.action)}
                      sx={{
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: 12, md: 13 },
                        px: { xs: 1.5, md: 2 },
                        py: { xs: 0.4, md: 0.6 },
                        whiteSpace: "nowrap",
                        borderColor: "#E2A4B2",
                        color: "#B3435A",
                        
                      }}
                    >
                      {item.action?.label || "Start"}
                    </Button>
                  )}
                </Paper>
              );
            })}
          </Box>
        ))}

        {(card?.disclaimers || []).length > 0 && (
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
            {card.disclaimers.join(" ")}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default WellnessCard;
